import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { cn } from '@/lib/cn';

export interface TabItem {
  key: string;
  label: ReactNode;
  /** When set, the tab is a route link instead of a button. */
  to?: string | undefined;
  end?: boolean | undefined;
}

export interface TabsProps {
  items: TabItem[];
  value?: string | undefined;
  onChange?: ((key: string) => void) | undefined;
  className?: string | undefined;
}

const tabClass = (on: boolean) =>
  cn(
    '-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 font-semibold transition-colors',
    on ? 'border-orange text-ink' : 'border-transparent text-ink3 hover:text-ink',
  );

export function Tabs({ items, value, onChange, className }: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'mb-4 flex gap-0.5 overflow-x-auto border-b border-line [scrollbar-width:none]',
        className,
      )}
    >
      {items.map((it) =>
        it.to ? (
          <NavLink
            key={it.key}
            to={it.to}
            end={it.end ?? false}
            role="tab"
            className={({ isActive }) => tabClass(isActive)}
          >
            {it.label}
          </NavLink>
        ) : (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={value === it.key}
            className={tabClass(value === it.key)}
            onClick={() => onChange?.(it.key)}
          >
            {it.label}
          </button>
        ),
      )}
    </div>
  );
}
